import httpClient, { IRequest, IResponse, TRequestHeaders } from '../../../apiService/httpClient';
import { MaterialType } from '../../enum/Material';
import { EProjectType } from '../../enum/project';
import { getLanguage } from '../../localization';
import { IMacra2TemplateRequestSerialized, IProjectMacra1Request, IProjectMacra2Request, ITemplateRequestSerialized } from '../IApiRequest';
import {
    IBlobFileResponse,
    ICompany,
    IMaterialResponse,
    IPaginatedResponse,
    IPreviewMaterialResponse,
    IPreviewTemplateResponse,
    IProjectBaseResponse,
    IProjectMacra1Response,
    IProjectMacra2Response,
    ITemplateMacra1Response,
    ITemplateMacra2Response,
    MaterialPreviewResponseWithSoilVelocity,
} from '../IApiResponse';

const BeMaccaferriStoreProvider = () => {
    let _baseURL = (process.env.ENVIRONMENT as string) === 'local' ? (process.env.STORE_BASE_URL as string as string) : '/';

    const _createRequest = <D>(_pathname: string, _queryString?: string, _data?: D): IRequest => {
        return {
            url: _queryString ? _pathname + '?' + _queryString : _pathname,
            baseURL: _baseURL,
            data: _data,
        };
    };

    const _createHeaders = (): TRequestHeaders => {
        return {
            'Accept-Language': getLanguage(),
        };
    };

    const _handlePagination = (pageNumber?: number, pageSize?: number, name?: string): string => {
        let query: string = '';
        if (!!pageNumber) query = query.concat(`PageNumber=${pageNumber}`);
        if (!!pageSize) query = query.concat(`&PageSize=${pageSize}`);
        if (!!name) query = query.concat(`&name=${name}`);
        return query;
    };

    //material------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------
    const getAllBaseMaterialAsync = async (pageNumber?: number, pageSize?: number, name?: string): Promise<IResponse<IPaginatedResponse<IPreviewMaterialResponse>>> =>
        httpClient.getAsync(_createRequest('api/Store/Material/GetAllBase', _handlePagination(pageNumber, pageSize, name)), _createHeaders());

    const getMaterialsByTypeAsync = async (type: MaterialType): Promise<IResponse<IMaterialResponse[]>> =>
        httpClient.getAsync(_createRequest(`api/Store/Material/GetMaterialsByType/${type}`), _createHeaders());

    const getMaterialByIdAsync = async (id: number): Promise<IResponse<IMaterialResponse>> => httpClient.getAsync(_createRequest(`api/Store/Material/Get/${id}`), _createHeaders());

    const getMaterialWithAllLanguagesByIdAsync = async (id: number): Promise<IResponse<IMaterialResponse>> =>
        httpClient.getAsync(_createRequest(`api/Store/Material/GetMaterialWithAllLanguages/${id}`), _createHeaders());

    const getNaturalSoilMaterialsWithVelocityAsync = async (): Promise<IResponse<MaterialPreviewResponseWithSoilVelocity[]>> =>
        httpClient.getAsync(_createRequest(`api/Store/Material/GetNaturalSoilMaterialsWithVelocity`), _createHeaders());

    const getIsAvailableSoilMaterials = async (): Promise<IResponse<IMaterialResponse[]>> => httpClient.getAsync(_createRequest('api/Store/Material/GetIsAvailableSoilMaterials'), _createHeaders());

    const getIsAvailableStillingBasinMaterials = async (): Promise<IResponse<IMaterialResponse[]>> =>
        httpClient.getAsync(_createRequest('api/Store/Material/GetIsAvailableStillingBasinMaterials'), _createHeaders());

    const addMaterialAsync = async (material: FormData): Promise<IResponse<boolean>> =>
        httpClient.postAsync(_createRequest('api/Store/Material/Add', '', material), { ..._createHeaders(), 'content-type': 'multipart/form-data', Accept: 'multipart/form-data' });

    const updateMaterialAsync = async (material: FormData): Promise<IResponse<boolean>> =>
        httpClient.putAsync(_createRequest('api/Store/Material/Update', '', material), { ..._createHeaders(), 'content-type': 'multipart/form-data', Accept: 'multipart/form-data' });

    const deleteMaterialByIdAsync = async (id: number): Promise<IResponse<boolean>> => httpClient.deleteAsync(_createRequest(`api/Store/Material/Delete/${id}`), _createHeaders());

    //project------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------
    const getProjectsByCompanyAsync = async (company: number, pageNumber: number, pageSize: number): Promise<IResponse<IPaginatedResponse<IProjectBaseResponse>>> =>
        httpClient.getAsync(_createRequest(`api/Store/Company/GetAllProjectsByCompany/${company}?PageNumber=${pageNumber}&PageSize=${pageSize}`), _createHeaders());

    const getProjectsByAuthorAsync = async (author: string, pageNumber: number, pageSize: number): Promise<IResponse<IPaginatedResponse<IProjectBaseResponse>>> =>
        httpClient.getAsync(_createRequest(`api/Store/Company/GetAllProjectsByAuthor/${author}?PageNumber=${pageNumber}&PageSize=${pageSize}`), _createHeaders());

    const projectAvailableByAuthorAsync = async (id: number, author: string): Promise<IResponse<boolean>> =>
        httpClient.getAsync(_createRequest(`api/Store/Company/ProjectAvailableByAuthor?projectId=${id}&author=${author}`), _createHeaders());

    const getProjectBaseAsync = async (id: number): Promise<IResponse<IProjectBaseResponse>> => httpClient.getAsync(_createRequest(`api/Store/Company/GetProjectBase/${id}`), _createHeaders());

    const deleteProjectByIdAsync = async (id: number): Promise<IResponse<boolean>> => httpClient.deleteAsync(_createRequest(`api/Store/Company/DeleteProject?projectId=${id}`), _createHeaders());
    //project - CBP
    const getProjectCBPByIdAsync = async (id: number): Promise<IResponse<IProjectMacra1Response>> => httpClient.getAsync(_createRequest(`api/Store/Company/GetProjectCBP/${id}`), _createHeaders());

    const addProjectCBPAsync = async (project: IProjectMacra1Request): Promise<IResponse<IProjectMacra1Response>> =>
        httpClient.postAsync(_createRequest('api/Store/Company/AddProjectCBP', '', project), _createHeaders());

    const updateProjectCBPAsync = async (project: IProjectMacra1Request, authorEmail: string): Promise<IResponse<IProjectMacra1Response>> =>
        httpClient.putAsync(_createRequest(`api/Store/Company/UpdateProjectCBP?authorEmail=${authorEmail}`, '', project), _createHeaders());
    //project - GCS
    const getProjectGCSByIdAsync = async (id: number): Promise<IResponse<IProjectMacra2Response>> => httpClient.getAsync(_createRequest(`api/Store/Company/GetProjectGCS/${id}`), _createHeaders());

    const addProjectGCSAsync = async (project: IProjectMacra2Request): Promise<IResponse<IProjectMacra2Response>> =>
        httpClient.postAsync(_createRequest('api/Store/Company/AddProjectGCS', '', project), _createHeaders());

    //TODO controlla interfaccia project
    const updateProjectGCSAsync = async (project: IProjectMacra2Request, authorEmail: string): Promise<IResponse<IProjectMacra2Response>> =>
        httpClient.putAsync(_createRequest(`api/Store/Company/UpdateProjectGCS?authorEmail=${authorEmail}`, '', project), _createHeaders());

    //company------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------
    const getCompanyByIdAsync = async (id: number): Promise<IResponse<ICompany>> => httpClient.getAsync(_createRequest(`api/Store/Company/GetCompany/${id}`), _createHeaders());

    const getCompanyByNameAsync = async (companyName: string): Promise<IResponse<ICompany>> =>
        httpClient.getAsync(_createRequest(`api/Store/Company/GetCompanyByName/${companyName}`), _createHeaders());

    const addCompanyAsync = async (company: ICompany): Promise<IResponse<boolean>> => httpClient.postAsync(_createRequest(`api/Store/Company/AddCompany`, '', company), _createHeaders());

    //template------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------
    const getTemplatesByTypeAsync = async (type: EProjectType, pageNumber?: number, pageSize?: number): Promise<IResponse<IPaginatedResponse<IPreviewTemplateResponse>>> =>
        httpClient.getAsync(_createRequest(`api/Store/Template/GetByType/${type}`, _handlePagination(pageNumber, pageSize)), _createHeaders());

    //template - CBP
    const getCBPWithAllLanguagesAsync = async (id: number): Promise<IResponse<ITemplateMacra1Response>> =>
        httpClient.getAsync(_createRequest(`api/Store/Template/GetCBPWithAllLanguages/${id}`), _createHeaders());

    const getCBPPreviewTemplatesAsync = async (pageNumber?: number, pageSize?: number, name?: string): Promise<IResponse<IPaginatedResponse<IPreviewTemplateResponse>>> =>
        httpClient.getAsync(_createRequest('api/Store/Template/GetCBPPreviewTemplates', _handlePagination(pageNumber, pageSize, name)), _createHeaders());

    const getTemplateCBPByIdAsync = async (id: number): Promise<IResponse<ITemplateMacra1Response>> => httpClient.getAsync(_createRequest(`api/Store/Template/GetCBP/${id}`), _createHeaders());

    const addCBPTemplateAsync = async (template: ITemplateRequestSerialized): Promise<IResponse<boolean>> =>
        httpClient.postAsync(_createRequest('api/Store/Template/AddCBP', '', template), { ..._createHeaders(), 'content-type': 'multipart/form-data', Accept: 'multipart/form-data' });

    const updateCBPTemplateAsync = async (template: ITemplateRequestSerialized): Promise<IResponse<boolean>> =>
        httpClient.putAsync(_createRequest('api/Store/Template/UpdateCBP', '', template), { ..._createHeaders(), 'content-type': 'multipart/form-data', Accept: 'multipart/form-data' });

    const deleteTemplateCBPByIdAsync = async (id: number): Promise<IResponse<boolean>> => httpClient.deleteAsync(_createRequest(`api/Store/Template/DeletePermanentlyCBP/${id}`), _createHeaders());

    //template - GCS
    const getGCSWithAllLanguagesAsync = async (id: number): Promise<IResponse<ITemplateMacra2Response>> =>
        httpClient.getAsync(_createRequest(`api/Store/Template/GetGCSWithAllLanguages/${id}`), _createHeaders());

    const getGCSPreviewTemplatesAsync = async (pageNumber?: number, pageSize?: number, name?: string): Promise<IResponse<IPaginatedResponse<IPreviewTemplateResponse>>> =>
        httpClient.getAsync(_createRequest('api/Store/Template/GetGCSPreviewTemplates', _handlePagination(pageNumber, pageSize, name)), _createHeaders());

    const getTemplateGCSByIdAsync = async (id: number): Promise<IResponse<ITemplateMacra2Response>> => httpClient.getAsync(_createRequest(`api/Store/Template/GetGCS/${id}`), _createHeaders());

    const addGCSTemplateAsync = async (template: IMacra2TemplateRequestSerialized): Promise<IResponse<boolean>> =>
        httpClient.postAsync(_createRequest('api/Store/Template/AddGCS', '', template), { ..._createHeaders(), 'content-type': 'multipart/form-data', Accept: 'multipart/form-data' });

    const updateGCSTemplateAsync = async (template: IMacra2TemplateRequestSerialized): Promise<IResponse<boolean>> =>
        httpClient.putAsync(_createRequest('api/Store/Template/UpdateGCS', '', template), { ..._createHeaders(), 'content-type': 'multipart/form-data', Accept: 'multipart/form-data' });

    const deleteTemplateGCSByIdAsync = async (id: number): Promise<IResponse<boolean>> => httpClient.deleteAsync(_createRequest(`api/Store/Template/DeletePermanentlyGCS/${id}`), _createHeaders());

    //blob------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------

    const getAllDocumentsAsync = async (): Promise<IResponse<IBlobFileResponse[]>> => httpClient.getAsync(_createRequest(`api/Store/Blob/GetAllDocuments`), _createHeaders());

    const uploadDocumentAsync = async (file: FormData): Promise<IResponse<boolean>> =>
        httpClient.putAsync(_createRequest('api/Store/Blob/UploadWithFilename', '', file), { ..._createHeaders(), 'content-type': 'multipart/form-data', Accept: 'multipart/form-data' });

    const deleteDocumentAsync = async (name: string): Promise<IResponse<boolean>> => httpClient.deleteAsync(_createRequest(`api/Store/Blob/Delete/${name}`), _createHeaders());

    return {
        getAllBaseMaterialAsync,
        getMaterialsByTypeAsync,
        getMaterialByIdAsync,
        getNaturalSoilMaterialsWithVelocityAsync,
        addMaterialAsync,
        updateMaterialAsync,
        deleteMaterialByIdAsync,
        getProjectsByCompanyAsync,
        getProjectsByAuthorAsync,
        projectAvailableByAuthorAsync,
        deleteProjectByIdAsync,
        getProjectBaseAsync,
        getProjectCBPByIdAsync,
        addProjectCBPAsync,
        updateProjectCBPAsync,
        getProjectGCSByIdAsync,
        addProjectGCSAsync,
        updateProjectGCSAsync,
        getCompanyByIdAsync,
        getCompanyByNameAsync,
        addCompanyAsync,
        getTemplatesByTypeAsync,
        deleteTemplateCBPByIdAsync,
        deleteTemplateGCSByIdAsync,
        getCBPWithAllLanguagesAsync,
        getCBPPreviewTemplatesAsync,
        getTemplateCBPByIdAsync,
        addCBPTemplateAsync,
        updateCBPTemplateAsync,
        getGCSWithAllLanguagesAsync,
        getGCSPreviewTemplatesAsync,
        getTemplateGCSByIdAsync,
        addGCSTemplateAsync,
        updateGCSTemplateAsync,
        getIsAvailableSoilMaterials,
        getIsAvailableStillingBasinMaterials,
        getMaterialWithAllLanguagesByIdAsync,
        getAllDocumentsAsync,
        uploadDocumentAsync,
        deleteDocumentAsync,
    };
};

export default BeMaccaferriStoreProvider();
